/**
 * Tasks Gantt toolbar for /schedule/tasks (title row, tools row, look-ahead status).
 * Does not own chart data or dialogs.
 */
import { Anchor, Button, Group, Select, Text, Tooltip } from '@mantine/core';
import { Link as RouterLink } from 'react-router-dom';
import {
  IconDeviceFloppy,
  IconLayoutSidebar,
  IconRefresh,
  IconTargetArrow,
} from '@tabler/icons-react';
import { GanttTaskSearchField } from '../components/GanttTaskSearchField';
import { GanttLiveBadge } from '../components/GanttLiveBadge';
import { GanttColumnsMenu } from '../components/GanttColumnsMenu';
import { GanttTasksMenu } from '../components/GanttTasksMenu';
import { GanttAnalysisMenu } from '../components/GanttAnalysisMenu';
import { GanttHelpMenu } from '../components/GanttHelpMenu';
import { GanttExportMenu } from '../components/GanttExportMenu';
import { GanttExpandCollapseButton } from '../components/GanttExpandCollapseButton';
import { GanttExportProgress } from '../components/GanttExportProgress';
import { GanttColumnFilterBar } from '../components/GanttColumnFilterBar';
import { ganttToolbarButtonProps } from '../components/ganttToolbarStyles';
import { GanttViewModeToggle } from '../components/targets';
import { ZoomControl } from '../components/ZoomControl';
import { saveShowGanttChartPreference } from '../../../gantt/ganttShowChartPreference';
import { PermissionRestrictedTooltip, PERMISSION_RESTRICTED_MESSAGE } from '../../../permissions/scheduler';
import { ENABLE_TARGETS_QUANTITY_PANEL } from '../ganttPageFlags';
import { APP_ROUTES } from '../../../navigation/appRoutes';
import { normalizeTaskSearchQuery } from '../../../gantt/taskNameSearch';
import { writeScheduleRecalcEnginePreference } from '../../../gantt/scheduleRecalcEnginePreference';

export type GanttPageToolbarProps = Record<string, any>;

export function GanttPageToolbar(props: GanttPageToolbarProps) {
  const {
    totalTaskCount,
    viewMode,
    handleViewModeChange,
    lookAheadPeriod,
    lookAheadPeriodOptions,
    setLookAheadPeriod,
    taskSearchInputRef,
    taskSearchQuery,
    setTaskSearchQuery,
    taskSearchExpanded,
    setTaskSearchExpanded,
    debouncedTaskSearch,
    taskNameSearchResult,
    handleInvalidateAndFetch,
    projectId,
    saving,
    discarding,
    storeLoading,
    autoScheduleEnabled,
    showCriticalPathOnly,
    handleAutoScheduleToggle,
    setShowCriticalPathOnly,
    manualRecalculateLoading,
    scheduleRecalcLoading,
    handleManualRecalculate,
    recalcEngine,
    selectedTaskId,
    hasUiRootTask,
    openAddTaskModal,
    setRecalcEngine,
    setKeyboardShortcutsOpen,
    toolsExportItems,
    allTasksExpanded,
    handleExpandAllToggle,
    toolsDataItems,
    setCriticalFilterEpoch,
    highlightCriticalPath,
    setHighlightCriticalPath,
    compareBaselinesEnabled,
    setCompareBaselinesEnabled,
    baselineCompareOptions,
    compareBaselineIndex,
    setCompareBaselineIndex,
    baselineVarianceSummary,
    compareBaselineLabel,
    handleOpenSetBaseline,
    baselineSubmitting,
    canSetBaseline,
    totalSessionChanges,
    exporting,
    revisionActionBusy,
    activeRevision,
    baselineInProgress,
    canMutateTasks,
    targetsShowFullWbs,
    setTargetsShowFullWbs,
    visibleOptionalColumns,
    columnFilterValues,
    setColumnFilterValues,
    assigneeFilterOptions,
    setVisibleOptionalColumns,
    setDiscardConfirmOpen,
    setSaveConfirmOpen,
    handleZoomLevelChange,
    zoomLevel,
    scrollGanttToToday,
    showGanttChart,
    setShowGanttChart,
    lookAheadLeafTaskIds,
    targetsData,
    setTargetsDiscardPending,
    exportProgress,
  } = props;
  return (
    <>
    {/* Row 1 — page chrome */}
    <div
      style={{
        padding: '12px 20px',
        background: 'var(--app-toolbar-bg)',
        flexShrink: 0,
      }}
    >
      <div className="gantt-tasks-title-bar">
        <div className="gantt-tasks-title-bar__primary">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 800,
              fontSize: 18,
              whiteSpace: 'nowrap',
            }}
          >
            Tasks
            <span
              style={{
                fontSize: 12,
                color: 'var(--app-text-muted)',
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              {totalTaskCount.toLocaleString()}
            </span>
          </span>
          <GanttViewModeToggle value={viewMode} onChange={handleViewModeChange} />
          {viewMode === 'targets' && lookAheadPeriodOptions?.length ? (
            <Group gap={6} wrap="nowrap" align="center">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                Period
              </Text>
              <Select
                aria-label="Look-ahead period"
                size="xs"
                w={120}
                allowDeselect={false}
                data={lookAheadPeriodOptions.map((choice: { value: string; label: string }) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                value={lookAheadPeriod}
                onChange={(value) => {
                  if (value === 'day' || value === 'week' || value === 'month') {
                    setLookAheadPeriod(value);
                  }
                }}
                comboboxProps={{ withinPortal: true }}
              />
            </Group>
          ) : null}
          <GanttTaskSearchField
            ref={taskSearchInputRef}
            value={taskSearchQuery}
            onChange={setTaskSearchQuery}
            expanded={taskSearchExpanded}
            onExpandedChange={setTaskSearchExpanded}
            matchCount={
              normalizeTaskSearchQuery(taskSearchQuery) &&
              normalizeTaskSearchQuery(taskSearchQuery) ===
                normalizeTaskSearchQuery(debouncedTaskSearch)
                ? (taskNameSearchResult?.matchCount ?? 0)
                : null
            }
          />
          <Tooltip
            label="Clear this project's local cache and reload the latest schedule from the server."
            multiline
            w={240}
            withArrow
          >
            <Button
              {...ganttToolbarButtonProps}
              variant="default"
              px={8}
              onClick={() => void handleInvalidateAndFetch()}
              aria-label="Invalidate and fetch"
              disabled={!projectId || saving || discarding || storeLoading}
            >
              <IconRefresh size={15} stroke={1.75} />
            </Button>
          </Tooltip>
          <GanttLiveBadge
            enabled={autoScheduleEnabled}
            criticalPathOnly={showCriticalPathOnly}
            onClick={() => handleAutoScheduleToggle(!autoScheduleEnabled)}
            onCriticalPathClick={() => {
              setShowCriticalPathOnly(false);
            }}
          />
          {!autoScheduleEnabled && viewMode === 'schedule' ? (
            <Button
              {...ganttToolbarButtonProps}
              variant="light"
              loading={manualRecalculateLoading || scheduleRecalcLoading}
              onClick={handleManualRecalculate}
              title={
                recalcEngine === 'MPXJ' || recalcEngine === 'CPM'
                  ? `Recalculate the full project schedule on the server (${recalcEngine})`
                  : selectedTaskId
                    ? 'Recalculate dependent tasks from the selected task'
                    : 'Recalculate dependent tasks for the whole project'
              }
            >
              Recalculate
            </Button>
          ) : null}
          {!hasUiRootTask && projectId ? (
            <Group gap={10} wrap="wrap" align="center">
              <Button {...ganttToolbarButtonProps} variant="light" onClick={() => openAddTaskModal(null)}>
                Add root task
              </Button>
              <Text size="xs" c="dimmed" maw={360}>
                Start from a blank schedule, or{' '}
                <Anchor component={RouterLink} to={APP_ROUTES.schedule.import} size="xs">
                  import an existing plan
                </Anchor>{' '}
                if you have a file.
              </Text>
            </Group>
          ) : null}
        </div>

        <div className="gantt-tasks-title-bar__trailing">
          <GanttHelpMenu
            recalcEngine={recalcEngine}
            onRecalcEngineChange={(engine) => {
              writeScheduleRecalcEnginePreference(engine);
              setRecalcEngine(engine);
            }}
            onOpenKeyboardShortcuts={() => setKeyboardShortcutsOpen(true)}
          />
          <GanttExportMenu items={toolsExportItems} />
        </div>
      </div>
    </div>

    {/* Row 2 — white tools bar, flush above the Gantt */}
    <div className="gantt-toolbar-row2">
        <div className="gantt-toolbar-row2__start">
          <GanttExpandCollapseButton
            expandedAll={allTasksExpanded}
            onToggle={handleExpandAllToggle}
          />
          <GanttTasksMenu items={toolsDataItems} />
          <GanttAnalysisMenu
            criticalPathOnly={showCriticalPathOnly}
            onCriticalPathOnlyChange={(enabled) => {
              setShowCriticalPathOnly(enabled);
              if (enabled) setCriticalFilterEpoch((n: number) => n + 1);
            }}
            highlightCriticalPath={highlightCriticalPath}
            onHighlightCriticalPathChange={(enabled) => {
              setHighlightCriticalPath(enabled);
              if (enabled) setCriticalFilterEpoch((n: number) => n + 1);
            }}
            compareBaselines={compareBaselinesEnabled}
            onCompareBaselinesChange={setCompareBaselinesEnabled}
              compareBaselinesDisabled={
                baselineCompareOptions.filter((o: { disabled?: boolean }) => !o.disabled).length === 0
              }
            compareBaselinesDisabledReason={
              baselineCompareOptions.length > 0
                ? 'All baselines for this project are deleted. Restore or set a new baseline to compare.'
                : 'No baseline data on this project yet. Use Set baseline first.'
            }
            compareBaselineIndex={compareBaselineIndex}
            onCompareBaselineIndexChange={setCompareBaselineIndex}
            compareBaselineOptions={baselineCompareOptions}
            compareVarianceSummary={baselineVarianceSummary}
            compareVarianceBaselineLabel={compareBaselineLabel}
            onSetBaseline={() => void handleOpenSetBaseline()}
            setBaselineLoading={baselineSubmitting}
            setBaselineDisabled={
              !canSetBaseline ||
              !projectId ||
              totalSessionChanges > 0 ||
              saving ||
              discarding ||
              exporting ||
              baselineSubmitting ||
              revisionActionBusy ||
              Boolean(activeRevision) ||
              baselineInProgress
            }
            setBaselineDisabledReason={
              !canSetBaseline
                ? PERMISSION_RESTRICTED_MESSAGE
                : totalSessionChanges > 0
                  ? 'Save your schedule changes to the server first. Baselines can only be created from the saved schedule.'
                  : baselineInProgress
                    ? 'A baseline job is already running — view progress in the request tracker.'
                    : activeRevision
                      ? 'An open baseline revision already exists — finish or discard it from the banner before creating another.'
                      : !projectId
                        ? 'No project is selected.'
                        : 'Create a new baseline from the schedule saved on the server.'
            }
            viewMode={viewMode}
            showFullWbs={targetsShowFullWbs}
            onShowFullWbsChange={setTargetsShowFullWbs}
          />
          <GanttColumnFilterBar
            visibleOptionalColumns={visibleOptionalColumns}
            values={columnFilterValues}
            onChange={setColumnFilterValues}
            filterOptionsByColumnId={{ assignees: assigneeFilterOptions }}
          />
          <GanttColumnsMenu
            value={visibleOptionalColumns}
            onChange={setVisibleOptionalColumns}
          />
          {!(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets') &&
          totalSessionChanges > 0 ? (
            <Button
              {...ganttToolbarButtonProps}
              variant="default"
              color="red"
              onClick={() => setDiscardConfirmOpen(true)}
              disabled={saving || discarding || baselineSubmitting}
              loading={discarding}
            >
              Discard changes
            </Button>
          ) : null}
          {!(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets') &&
          totalSessionChanges > 0 ? (
            <PermissionRestrictedTooltip restricted={!canMutateTasks}>
              <Button
                {...ganttToolbarButtonProps}
                variant="filled"
                color="brand"
                leftSection={<IconDeviceFloppy size={14} />}
                onClick={() => setSaveConfirmOpen(true)}
                disabled={
                  !canMutateTasks || saving || discarding || baselineSubmitting
                }
                loading={saving}
              >
                {saving
                  ? 'Saving to server...'
                  : `Save to Server (${totalSessionChanges})`}
              </Button>
            </PermissionRestrictedTooltip>
          ) : null}
        </div>
        <div className="gantt-toolbar-row2__end">
          <div
            className="gantt-toolbar-row2__chart-tools"
            data-visible={showGanttChart ? 'true' : 'false'}
            aria-hidden={!showGanttChart}
          >
            <ZoomControl value={zoomLevel} onChange={handleZoomLevelChange} />
            <Tooltip label="Scroll the chart to today" withArrow>
              <Button
                {...ganttToolbarButtonProps}
                variant="default"
                onClick={scrollGanttToToday}
                aria-label="Scroll gantt to today"
                tabIndex={showGanttChart ? undefined : -1}
              >
                Today
              </Button>
            </Tooltip>
          </div>
          <Tooltip
            label={
              showGanttChart
                ? 'Show or hide the timeline chart. Off shows the task grid only.'
                : 'Show the timeline chart beside the task grid.'
            }
            withArrow
            multiline
            w={220}
          >
            <Button
              {...ganttToolbarButtonProps}
              variant={showGanttChart ? 'filled' : 'default'}
              color={showGanttChart ? 'brand' : undefined}
              px={8}
              aria-label={showGanttChart ? 'Hide Gantt chart' : 'Show Gantt chart'}
              aria-pressed={showGanttChart}
              onClick={() => {
                const next = !showGanttChart;
                saveShowGanttChartPreference(next);
                setShowGanttChart(next);
              }}
            >
              <IconLayoutSidebar size={15} stroke={1.75} />
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Look-ahead status stays under the toolbar. Schedule save/discard sit next to Columns. */}
      {viewMode === 'targets' ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 8,
          flexWrap: 'wrap',
          padding: '0 20px 8px',
          background: 'var(--app-card-bg, var(--mantine-color-body))',
          flexShrink: 0,
        }}
      >
        <Group gap={8} wrap="nowrap" align="center">
          {viewMode === 'targets' && !targetsShowFullWbs && (lookAheadLeafTaskIds?.size ?? 0) === 0 ? (
            <Text size="xs" c="dimmed">
              No tasks in look-ahead — change Period or turn on Full WBS in Analysis
            </Text>
          ) : null}
          {ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets' ? (
            <>
              {targetsData.loading ? (
                <Text size="xs" c="dimmed">
                  Loading look ahead…
                </Text>
              ) : null}
              <Text
                size="xs"
                c="dimmed"
                title="Leaf tasks overlapping the look-ahead window for the current Period"
                style={{ whiteSpace: 'nowrap' }}
              >
                {`Showing ${lookAheadLeafTaskIds?.size ?? 0} task${
                  (lookAheadLeafTaskIds?.size ?? 0) === 1 ? '' : 's'
                }`}
              </Text>
              {targetsData.dirtyCount > 0 ? (
                <Button
                  {...ganttToolbarButtonProps}
                  variant="default"
                  color="red"
                  disabled={
                    targetsData.loading ||
                    targetsData.saving ||
                    targetsData.recalculating
                  }
                  onClick={() => setTargetsDiscardPending({ kind: 'drafts' })}
                >
                  Discard changes
                </Button>
              ) : null}
              <PermissionRestrictedTooltip restricted={!canMutateTasks}>
                <Button
                  {...ganttToolbarButtonProps}
                  variant="filled"
                  color="brand"
                  leftSection={<IconDeviceFloppy size={14} />}
                  loading={targetsData.saving}
                  disabled={
                    !canMutateTasks ||
                    targetsData.dirtyCount === 0 ||
                    targetsData.loading ||
                    targetsData.recalculating
                  }
                  onClick={() => void targetsData.save()}
                >
                  {targetsData.dirtyCount > 0
                    ? `Save look ahead (${targetsData.dirtyCount})`
                    : 'Save look ahead'}
                </Button>
              </PermissionRestrictedTooltip>
              <Button
                {...ganttToolbarButtonProps}
                variant="default"
                leftSection={<IconRefresh size={14} />}
                onClick={targetsData.refresh}
                disabled={
                  targetsData.loading ||
                  targetsData.saving ||
                  targetsData.recalculating
                }
              >
                Refresh look ahead
              </Button>
              {zoomLevel === 'day' ? (
                <PermissionRestrictedTooltip restricted={!canMutateTasks}>
                  <Button
                    {...ganttToolbarButtonProps}
                    variant="default"
                    leftSection={<IconTargetArrow size={14} />}
                    loading={targetsData.recalculating}
                    disabled={
                      !canMutateTasks ||
                      targetsData.loading ||
                      targetsData.saving ||
                      targetsData.recalculating
                    }
                    title="Auto-generate daily quantities for the current month (same as DPR Re-Calculate Targets)"
                    onClick={() => void targetsData.recalculate()}
                  >
                    Re-Calculate look ahead
                  </Button>
                </PermissionRestrictedTooltip>
              ) : null}
            </>
          ) : null}
        </Group>
      </div>
      ) : null}
      <GanttExportProgress visible={exporting} percent={exportProgress} />
    </>
  );
}
